import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Check, Lock } from "lucide-react";
import {
  getCourse,
  type CourseDetail,
  type ModuleListItem,
} from "../api/courses";
import {
  myCourseProgress,
  type CourseProgressDetailOut,
} from "../api/assignments";
import { ApiError } from "../api/client";
import { fileUrl } from "../api/files";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { MobileTabBar } from "../components/MobileTabBar";

export function StudentCoursePage() {
  const { courseId } = useParams<{ courseId: string }>();
  const id = Number(courseId);
  const { user } = useCurrentUser();
  const [course, setCourse] = useState<CourseDetail | null>(null);
  const [progress, setProgress] = useState<CourseProgressDetailOut | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!Number.isFinite(id)) {
      setError("Некорректный курс");
      return;
    }
    setError(null);
    try {
      const [c, p] = await Promise.all([getCourse(id), myCourseProgress(id)]);
      setCourse(c);
      setProgress(p);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403)
        setError("Нет доступа к курсу");
      else if (err instanceof ApiError && err.status === 404)
        setError("Курс не найден");
      else
        setError(
          err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна",
        );
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!user) return null;

  if (error) {
    return (
      <div className="min-h-full bg-surface pb-20 md:pb-8">
        <TopBar />
        <div className="mx-auto max-w-3xl px-4 py-12 text-center text-ink-muted">
          <p>{error}</p>
          <Link to="/my-courses" className="mt-4 inline-block text-sm text-brand hover:underline">
            ← к моим курсам
          </Link>
        </div>
        <MobileTabBar active="courses" />
      </div>
    );
  }

  if (!course || !progress) {
    return (
      <div className="min-h-full bg-surface pb-20 md:pb-8">
        <TopBar />
        <main className="mx-auto max-w-3xl px-4 pt-4">
          <div className="h-48 animate-pulse rounded-2xl bg-surface-alt" />
          <div className="mt-6 h-4 w-32 animate-pulse rounded bg-surface-alt" />
          <div className="mt-3 h-24 animate-pulse rounded-xl bg-surface-alt" />
        </main>
        <MobileTabBar active="courses" />
      </div>
    );
  }

  const ctaModuleProgress = pickContinueModule(progress.modules);
  const ctaLesson =
    ctaModuleProgress
      ? (progress.lessons.find(
          (l) =>
            l.module_id === ctaModuleProgress.module_id &&
            l.status !== "completed",
        ) ??
        progress.lessons.find(
          (l) => l.module_id === ctaModuleProgress.module_id,
        ))
      : null;
  const ctaTarget = ctaLesson ? `/learn/lessons/${ctaLesson.lesson_id}` : null;
  const courseSubtitle = course.is_mandatory ? "Базовый курс" : "Маршрут";

  return (
    <div className="min-h-full bg-surface pb-32 md:pb-8">
      <TopBar />

      <main className="mx-auto max-w-3xl px-4 pt-4">
        {/* Hero */}
        <section className="overflow-hidden rounded-2xl bg-brand text-white shadow-sm">
          {course.cover_file_id ? (
            <div className="relative aspect-[16/9] w-full">
              <img
                src={fileUrl(course.cover_file_id)}
                alt=""
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/95 via-brand/70 to-brand/20" />
              <div className="absolute inset-x-0 bottom-0 p-4">
                <HeroBody
                  title={course.title}
                  subtitle={courseSubtitle}
                  percent={progress.percent}
                  completed={progress.lessons_completed}
                  total={progress.lessons_total}
                />
              </div>
            </div>
          ) : (
            <div className="p-5">
              <HeroBody
                title={course.title}
                subtitle={courseSubtitle}
                percent={progress.percent}
                completed={progress.lessons_completed}
                total={progress.lessons_total}
              />
            </div>
          )}
        </section>

        {/* About */}
        {course.description && (
          <section className="mt-5">
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-ink-muted">
              О курсе
            </h2>
            <p className="text-sm leading-relaxed text-ink">
              {course.description}
            </p>
          </section>
        )}

        {/* Modules */}
        <section className="mt-6">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-muted">
            Модули курса
          </h2>
          <ol className="space-y-2">
            {course.modules.map((m, idx) => (
              <li key={m.id}>
                <ModuleRow
                  course={course}
                  module={m}
                  index={idx}
                  progress={progress}
                />
              </li>
            ))}
          </ol>
        </section>
      </main>

      {/* Bottom CTA */}
      {ctaTarget && (
        <div className="fixed inset-x-0 bottom-14 z-10 border-t border-line bg-white/95 px-4 py-3 backdrop-blur md:bottom-0">
          <div className="mx-auto max-w-3xl">
            <Link
              to={ctaTarget}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-dark"
            >
              {progress.lessons_completed === 0
                ? "Начать обучение"
                : "Продолжить обучение"}
              <ChevronRight size={16} strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      )}

      <MobileTabBar active="courses" />
    </div>
  );
}

function TopBar() {
  return (
    <header className="sticky top-0 z-10 border-b border-line bg-surface/95 backdrop-blur">
      <div className="mx-auto flex max-w-3xl items-center px-4 py-3">
        <Link
          to="/my-courses"
          className="grid h-9 w-9 place-items-center rounded-lg text-ink-soft transition hover:bg-surface-alt"
          aria-label="Назад"
        >
          <ChevronLeft size={20} strokeWidth={2} />
        </Link>
        <h1 className="flex-1 text-center text-base font-semibold text-ink">
          Курс
        </h1>
        <span className="h-9 w-9" aria-hidden />
      </div>
    </header>
  );
}

function HeroBody({
  title,
  subtitle,
  percent,
  completed,
  total,
}: {
  title: string;
  subtitle: string;
  percent: number;
  completed: number;
  total: number;
}) {
  return (
    <>
      <div className="text-xl font-bold leading-snug drop-shadow-sm">
        {title}
      </div>
      <div className="mt-0.5 inline-flex items-center gap-2">
        <span className="rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide">
          {subtitle}
        </span>
      </div>
      <div className="mt-3 text-xs text-white/80">Ваш прогресс</div>
      <div className="mt-1 flex items-center gap-3">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/20">
          <div
            className="h-full rounded-full bg-white transition-all"
            style={{ width: `${percent}%` }}
          />
        </div>
        <span className="shrink-0 text-sm font-semibold tabular-nums">
          {percent}%
        </span>
      </div>
      <div className="mt-1 text-xs text-white/75">
        {completed} из {total} уроков пройдено
      </div>
    </>
  );
}

function ModuleRow({
  course,
  module: m,
  index,
  progress,
}: {
  course: CourseDetail;
  module: ModuleListItem;
  index: number;
  progress: CourseProgressDetailOut;
}) {
  const moduleProgress = progress.modules.find((mp) => mp.module_id === m.id);
  const percent = moduleProgress?.percent ?? 0;
  const completed = moduleProgress?.lessons_completed ?? 0;
  const total = moduleProgress?.lessons_total ?? 0;
  const isDone = total > 0 && completed === total;
  const isLocked = isModuleLocked(course, m, index, progress);

  const target = `/learn/modules/${m.id}`;

  const inner = (
    <div className="flex items-center gap-3 rounded-xl bg-white px-4 py-3 ring-1 ring-line transition group-hover:shadow-sm">
      <div
        className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm font-semibold ${
          isLocked
            ? "bg-surface-alt text-ink-muted"
            : isDone
              ? "bg-brand-success text-white"
              : "bg-brand/10 text-brand"
        }`}
      >
        {isLocked ? (
          <Lock size={14} strokeWidth={2.25} />
        ) : isDone ? (
          <Check size={16} strokeWidth={2.5} />
        ) : (
          <span>{index + 1}</span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-ink">{m.title}</div>
        <div className="mt-0.5 flex items-center gap-2 text-[11px] text-ink-muted">
          {isLocked ? (
            <span>Откроется после предыдущего модуля</span>
          ) : (
            <>
              <span className="tabular-nums">{percent}%</span>
              <span>·</span>
              <span>
                {completed} из {total}
              </span>
            </>
          )}
        </div>
      </div>
      {!isLocked && (
        <ChevronRight
          size={16}
          strokeWidth={2}
          className="shrink-0 text-ink-muted"
        />
      )}
    </div>
  );

  if (isLocked) {
    return <div className="block opacity-60">{inner}</div>;
  }
  return (
    <Link to={target} className="group block">
      {inner}
    </Link>
  );
}

function pickContinueModule(
  modules: CourseProgressDetailOut["modules"],
): CourseProgressDetailOut["modules"][number] | null {
  // First module that's not yet 100% done; if all done, return null
  for (const m of modules) {
    if (m.lessons_total > 0 && m.lessons_completed < m.lessons_total) return m;
  }
  return null;
}

function isModuleLocked(
  _course: CourseDetail,
  _module: ModuleListItem,
  index: number,
  progress: CourseProgressDetailOut,
): boolean {
  // No prerequisite system yet — only first incomplete module + finished modules
  // are unlocked. Any later module stays locked until previous ones are done.
  if (index === 0) return false;
  for (let i = 0; i < index; i++) {
    const prev = progress.modules[i];
    if (!prev) return true;
    if (prev.lessons_total > 0 && prev.lessons_completed < prev.lessons_total) {
      return true;
    }
  }
  return false;
}
